import { type ReactNode, type RefObject, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { LuX } from 'react-icons/lu'
import { cn } from '../../lib/cn.ts'
import { hint } from '../../lib/hint.ts'
import { IconButton } from './IconButton.tsx'

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

/** Open overlays, innermost last: only the top one handles Escape and traps Tab. */
const stack: symbol[] = []

/**
 * Focus management for a modal surface: moves focus in on open (to `initial` or the first
 * focusable), traps Tab inside, closes on Escape, and restores focus to the opener on close.
 */
export function useDialogFocus(
  ref: RefObject<HTMLElement | null>,
  onClose: () => void,
  initial?: RefObject<HTMLElement | null>,
) {
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount/unmount only
  useEffect(() => {
    const token = Symbol('dialog')
    stack.push(token)
    const opener = document.activeElement as HTMLElement | null
    const root = ref.current
    const first = initial?.current ?? root?.querySelector<HTMLElement>(FOCUSABLE) ?? root
    first?.focus({ preventScroll: true })
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== token || !root) return
      if (e.key === 'Escape') {
        e.preventDefault()
        closeRef.current()
        return
      }
      if (e.key !== 'Tab') return
      const items = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      )
      if (items.length === 0) return
      const firstEl = items[0]
      const lastEl = items[items.length - 1]
      if (
        e.shiftKey &&
        (document.activeElement === firstEl || !root.contains(document.activeElement))
      ) {
        e.preventDefault()
        lastEl?.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      stack.splice(stack.indexOf(token), 1)
      if (stack.length === 0) document.body.style.overflow = prevOverflow
      if (opener?.isConnected) opener.focus({ preventScroll: true })
    }
  }, [])
}

export interface ModalProps {
  title: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  /** Max width in px (DESIGN: 640 for Raise, 540 for the wait prompt). */
  width?: number
  initialFocus?: RefObject<HTMLElement | null>
  className?: string
}

/** Centred-top modal with backdrop (DESIGN.md §3.6). Esc, backdrop and X close it. */
export function Modal({
  title,
  onClose,
  children,
  footer,
  width = 640,
  initialFocus,
  className,
}: ModalProps) {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useDialogFocus(ref, onClose, initialFocus)
  return createPortal(
    <div className="fixed inset-0 z-60 flex animate-fade items-start justify-center overflow-y-auto bg-backdrop px-3 pt-[max(40px,env(safe-area-inset-top))] pb-[max(24px,env(safe-area-inset-bottom))] max-[720px]:pt-3">
      <div aria-hidden className="fixed inset-0" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ maxWidth: width }}
        className={cn(
          'relative w-full animate-fade rounded-modal border border-border bg-surface shadow-pop',
          className,
        )}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
          <h2 id={titleId} className="text-[17px]">
            {title}
          </h2>
          <IconButton aria-label="Close" onClick={onClose} {...hint('Close (Esc)')}>
            <LuX />
          </IconButton>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <div className="flex flex-wrap justify-end gap-2 border-t border-border px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
